import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Field } from '~/components/promotion/editor-section';
import { ToggleMini } from '~/components/toggle-mini';
import { cn } from '@/lib/utils';
import type { PromotionAction } from '~/lib/schemas/actions';

type Action = Extract<PromotionAction, { type: 'shipping-discount' }>;
type Rule = Action['rules'][number];

interface Props {
	value: Action;
	onChange: (next: Action) => void;
}

const MOCK_SHIPPING_METHODS = [
	{ id: 1, label: 'ELTA Courier' },
	{ id: 2, label: 'BoxNow' },
	{ id: 3, label: 'Παραλαβή από κατάστημα' }
];

/** Rule chain — each rule is a numbered sub-card; flags may both be ON (legacy parity). */
export function ShippingDiscountBody({ value, onChange }: Props) {
	const { t } = useTranslation();
	const setRule = (id: string, patch: Partial<Rule>) =>
		onChange({ ...value, rules: value.rules.map(r => (r.id === id ? { ...r, ...patch } : r)) });
	const removeRule = (id: string) => onChange({ ...value, rules: value.rules.filter(r => r.id !== id) });
	const addRule = () =>
		onChange({
			...value,
			rules: [
				...value.rules,
				{ id: crypto.randomUUID(), shippingMethodId: null, discountType: 'percentage', amount: 0, stopProcessing: false, additive: false }
			]
		});

	return (
		<div className="flex flex-col gap-3">
			{value.rules.map((rule, index) => (
				<div key={rule.id} className="rounded-lg border border-l-4 border-border border-l-brand bg-elevated p-3">
					<div className="mb-3 flex items-center gap-2">
						<span className="flex h-5 w-5 items-center justify-center rounded bg-brand text-[11px] font-semibold text-on-brand">
							{index + 1}
						</span>
						<span className="text-xs font-medium text-foreground-muted">{t('promotion.action.shipping.rule')}</span>
						<div className="flex-1" />
						<button
							type="button"
							onClick={() => removeRule(rule.id)}
							className="rounded-md p-1 text-foreground-muted hover:bg-muted hover:text-foreground"
							aria-label="remove rule">
							<X size={14} />
						</button>
					</div>
					<div className="grid grid-cols-3 gap-3">
						<Field label={t('promotion.action.shipping.method')}>
							<Select
								value={rule.shippingMethodId ? String(rule.shippingMethodId) : ''}
								onValueChange={v => setRule(rule.id, { shippingMethodId: Number(v) })}>
								<SelectTrigger>
									<SelectValue placeholder="—" />
								</SelectTrigger>
								<SelectContent>
									{MOCK_SHIPPING_METHODS.map(m => (
										<SelectItem key={m.id} value={String(m.id)}>
											{m.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
						<Field label={t('promotion.action.shipping.type')}>
							<Select value={rule.discountType} onValueChange={v => setRule(rule.id, { discountType: v as Rule['discountType'] })}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="percentage">{t('promotion.action.shipping.typePercentage')}</SelectItem>
									<SelectItem value="fixed">{t('promotion.action.shipping.typeFixed')}</SelectItem>
									<SelectItem value="free">{t('promotion.action.shipping.typeFree')}</SelectItem>
								</SelectContent>
							</Select>
						</Field>
						<Field label={t('promotion.action.shipping.amount')}>
							<Input
								type="number"
								min={0}
								disabled={rule.discountType === 'free'}
								value={rule.amount}
								onChange={e => setRule(rule.id, { amount: Number(e.target.value) || 0 })}
							/>
						</Field>
					</div>
					<div className="mt-3 flex gap-4">
						<ToggleMini
							label={t('promotion.action.shipping.stopProcessing')}
							checked={rule.stopProcessing}
							onCheckedChange={stopProcessing => setRule(rule.id, { stopProcessing })}
						/>
						<ToggleMini
							label={t('promotion.action.shipping.additive')}
							checked={rule.additive}
							onCheckedChange={additive => setRule(rule.id, { additive })}
						/>
					</div>
				</div>
			))}
			<button
				type="button"
				onClick={addRule}
				className={cn(
					'flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border-strong py-2 text-sm text-foreground-muted',
					'transition-colors hover:border-brand hover:bg-brand-subtle hover:text-brand-subtle-fg'
				)}>
				<Plus size={14} /> {t('promotion.action.shipping.addRule')}
			</button>
		</div>
	);
}
